import type { CatalogModel } from '@/types/models';

export function modelId(cm: CatalogModel): string {
  return cm.id;
}

export function displayName(cm: CatalogModel): string {
  return cm.display_name ?? cm.id;
}

export function routingTier(cm: CatalogModel): string {
  return cm.routing_tier ?? '';
}

const KNOWN_TIERS = ['official', 'stable', 'dev', 'bltcy', 'computorail'];

/** Host-like Catalog prefixes / Image route-pin suffixes (parallel to relay hosts). */
export const HOST_CATEGORIES = ['computorail', 'bltcy'] as const;

export const IMAGE_HOST_SUFFIXES = [
  '-computorail',
  '-yunwu',
  '-ssvip',
  '-apiyi',
  '-teamo',
  '-direct',
] as const;


export function baseModelName(cm: CatalogModel): string {
  const parts = cm.id.split('/');
  if (KNOWN_TIERS.includes(parts[0])) return parts.slice(1).join('/');
  for (const suffix of IMAGE_HOST_SUFFIXES) {
    if (cm.id.endsWith(suffix)) return cm.id.slice(0, -suffix.length);
  }
  return cm.id;
}

/** Host/category chip value for Catalog filter (computorail, bltcy, or image pin host). */
export function hostCategory(cm: CatalogModel): string | null {
  const tier = cm.routing_tier ?? '';
  if ((HOST_CATEGORIES as readonly string[]).includes(tier)) return tier;
  const head = cm.id.split('/', 1)[0];
  if ((HOST_CATEGORIES as readonly string[]).includes(head)) return head;
  for (const suffix of IMAGE_HOST_SUFFIXES) {
    if (cm.id.endsWith(suffix)) return suffix.slice(1);
  }
  return null;
}

/** Image route pins listed on a bare Catalog image model (from source_metadata.aliases). */
export function imageHostPinIds(cm: CatalogModel): string[] {
  if (cm.capability.modality !== 'image') return [];
  const aliases = cm.source_metadata?.aliases;
  if (!Array.isArray(aliases)) return [];
  return aliases.filter(
    (a): a is string =>
      typeof a === 'string' &&
      IMAGE_HOST_SUFFIXES.some((suffix) => a.endsWith(suffix)),
  );
}

export const DEFAULT_EVAL_JUDGER_MODEL_ID = 'official/gpt-5-nano';
export const EVAL_JUDGER_FALLBACK_MODEL_IDS = [
  DEFAULT_EVAL_JUDGER_MODEL_ID,
  'official/gpt-4o-mini',
  'official/gpt-4o',
];

const PREFERRED_EVAL_JUDGER_MODEL_IDS = EVAL_JUDGER_FALLBACK_MODEL_IDS;

/** Guarantee a routing-tier prefix; an id whose head is not a known tier
 *  defaults to `stable/`. Mirrors the backend `ensure_tier_prefix`. */
export function ensureTierPrefix(id: string): string {
  if (!id || KNOWN_TIERS.includes(id.split('/', 1)[0])) return id;
  return `stable/${id}`;
}

/** Match a stored model id against a catalog id, tolerant of a missing or
 *  unknown tier prefix (both sides default to `stable/`). */
export function sameModelId(a: string, b: string): boolean {
  return ensureTierPrefix(a) === ensureTierPrefix(b);
}

export function getDefaultJudgerModel(models: ReadonlyArray<{ value: string }>): string {
  if (models.length === 0) return DEFAULT_EVAL_JUDGER_MODEL_ID;

  for (const preferred of PREFERRED_EVAL_JUDGER_MODEL_IDS) {
    const found = models.find((model) => model.value === preferred);
    if (found) return found.value;
  }

  return ensureTierPrefix(models[0].value);
}

export function isDeepEvalCompatibleModel(modelIdValue: string): boolean {
  if (!modelIdValue) return false;

  const modelName = modelIdValue.slice(modelIdValue.lastIndexOf('/') + 1).toLowerCase();
  return modelName.startsWith('gpt-') || /^o[0-9]/.test(modelName);
}

export function organization(cm: CatalogModel): string {
  return cm.organization ?? '';
}

export function isText(cm: CatalogModel): boolean {
  return cm.capability.modality === 'text';
}

export function supportsVision(cm: CatalogModel): boolean {
  return cm.capability.modality === 'text' && cm.capability.supports_vision;
}

export function supportsReasoning(cm: CatalogModel): boolean {
  return cm.capability.modality === 'text' && cm.capability.supports_reasoning;
}

export function supportsFunctionCalling(cm: CatalogModel): boolean {
  return cm.capability.modality === 'text' && cm.capability.supports_function_calling;
}

export function perTokenInUsd1m(cm: CatalogModel): number | null {
  if (cm.billing.billing_mode !== 'per_token') return null;
  return cm.billing.input_per_million;
}

export function perTokenOutUsd1m(cm: CatalogModel): number | null {
  if (cm.billing.billing_mode !== 'per_token') return null;
  return cm.billing.output_per_million;
}
